import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchBettingItems, validatePhoneNumber } from '../api/api';
import BettingItemCard from '../components/BettingItemCard';
import LoadingSpinner from '../components/LoadingSpinner';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await fetchBettingItems();
      setItems(data);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    navigate(`/play/${item.id}`, { state: { item } });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhoneNumber(value);
      setPhoneError('');
    }
  };

  const handleHistorySubmit = (e) => {
    e.preventDefault();
    const formattedPhone = phoneNumber.startsWith('0') ? phoneNumber : `0${phoneNumber}`;
    
    if (!validatePhoneNumber(formattedPhone)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    
    navigate('/history', { state: { phoneNumber: formattedPhone } });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg">
          <div className="hero-glow hero-glow-1" />
          <div className="hero-glow hero-glow-2" />
          <div className="hero-pattern" />
        </div>
        
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
        >
          <motion.span 
            className="hero-badge"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            🎯 Kenya's #1 Lucky Draw Platform
          </motion.span>
          
          <h1 className="hero-title">
            <span className="title-line">Pick a Box.</span>
            <span className="title-line highlight">Win Big.</span>
          </h1>
          
          <p className="hero-description">
            Choose your lucky box for just <strong>KES 20</strong> and stand a chance 
            to win amazing prizes including cash, cars, and the latest smartphones!
          </p>

          <motion.div 
            className="hero-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="stat-item">
              <span className="stat-value">1M+</span>
              <span className="stat-label">Winners</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-value">KES 50M+</span>
              <span className="stat-label">Paid Out</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-value">24/7</span>
              <span className="stat-label">Play Anytime</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Betting Items Section */}
      <section className="items-section">
        <div className="section-header">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Choose Your Game
          </motion.h2>
          <motion.p 
            className="section-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Select a category and pick your lucky box
          </motion.p>
        </div>

        {loading ? (
          <LoadingSpinner text="Loading games..." />
        ) : (
          <motion.div 
            className="items-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {items.map((item, index) => (
              <BettingItemCard
                key={item.id}
                item={item}
                onClick={handleItemClick}
                index={index}
              />
            ))}
          </motion.div>
        )}
      </section>

      {/* History Access Section */}
      <section className="history-access-section">
        <motion.div 
          className="history-card"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="history-icon">📜</div>
          <div className="history-info">
            <h3>Check Your Betting History</h3>
            <p>Enter your phone number to view all your past bets and results</p>
          </div>
          <form className="history-form" onSubmit={handleHistorySubmit}>
            <div className="history-input-wrapper">
              <span className="input-prefix">+254</span>
              <input
                type="tel"
                placeholder="712345678"
                value={phoneNumber}
                onChange={handlePhoneChange}
                className={phoneError ? 'error' : ''}
              />
            </div>
            <motion.button
              type="submit"
              className="history-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View History
            </motion.button>
          </form>
          {phoneError && <span className="input-error">{phoneError}</span>}
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-header">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            How It Works
          </motion.h2>
        </div>

        <div className="steps-container">
          {[
            { icon: '🎮', title: 'Choose a Game', description: 'Select from Cash, Cars, Phones, and more exciting categories' },
            { icon: '📦', title: 'Pick Your Box', description: 'Trust your instincts and select one of the 7 mystery boxes' },
            { icon: '💳', title: 'Pay KES 20', description: 'Complete payment via M-Pesa STK Push on your phone' },
            { icon: '🎉', title: 'Win Instantly', description: 'Your box opens immediately to reveal your prize!' }
          ].map((step, index) => (
            <motion.div
              key={index}
              className="step-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
            >
              <span className="step-number">{index + 1}</span>
              <span className="step-icon">{step.icon}</span>
              <h4 className="step-title">{step.title}</h4>
              <p className="step-description">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <motion.div 
          className="cta-content"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <span className="cta-icon">🎁</span>
          <h2 className="cta-title">Ready to Try Your Luck?</h2>
          <p className="cta-description">
            Pick your first box now and you could be our next big winner!
          </p>
          <motion.button
            className="cta-button"
            onClick={() => window.scrollTo({ top: 500, behavior: 'smooth' })}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Playing Now
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;

